import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Tabs, Button, Tooltip, CloseOutlined, message } from "src/assets/imports/antdComponents";
import ReferenceItem from "../ReferenceItem";
import TranscriptView from "../TranscriptView";
import {
	IReferenceDrawerProps,
	IReferenceItem,
	ITranscriptItem,
	ReferenceDrawerVisibilityType,
	StudyType,
	StudyTypeDisplayName,
	ICitationReference,
	IDialogueItem,
	IAudioVideoNarration,
	IAudioVideoTranscriptItem,
	IQuantCitationRef,
	MediaType,
	AskKuralStudyTypeMapping,
} from "../../models/interfaces";
import { transformNameWithDateTime } from "src/pages/Studies/transformer";
import { downloadReferenceFile, getReference } from "../../serviceHelper";
import { downloadFile } from "src/pages/Studies/serviceHelper";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/store";
import ReportView from "../ReportView";
import "./style.css";
import useIndexedDB from "src/components/useIndexDB";
import { assertSafeHttpUrl } from "src/utils/security";
import { resetActiveCitationReferences, setReferenceDrawerVisibility, clearAutoOpenTranscriptItem } from "../../states/askKuralSlice";
import { ActionTypes, downloadFormats, FeatureKeysType, fileTypes, IFileDownloadPayload, ModuleTypes, SupportedLanguageType } from "src/pages/Studies/models/interfaces";
import { setCountSelectedQuote, setCountWidgetOpen } from "../../states/countWidgetSlice";
import { setReportsReferenceDrawerVisibility } from "src/pages/Reports/states/reportsSlice";
import { useActionTracking } from "src/components/UserMonitoring/useActionTracking";
import ReflectiveTranscriptView from "../ReflectiveTranscriptView";
import ReflectiveReferenceItem from "../ReflectiveReferenceItem";
import QuantCitationDrawer from "../QuantCitationDrawer";
import { isQuantCitation, extractQuantCitation } from "../../transformer";
import ReflectiveNotesReferenceItem from "../ReflectiveNotesReferenceItem";
import AudioVideoTranscriptView from "../AudioVideoTranscriptView";
import { isVideoMedia } from "../audioVideoUtils";
import { useFeatures } from "src/config/featureManager";

const extractContextIds = (item: IReferenceItem): number[] => {
	const contextIds: number[] = [];

	// OLD FORMAT: Use context array
	if (item.context && Array.isArray(item.context)) {
		item.context.forEach((ctx) => {
			if (ctx.dialog_no !== undefined) {
				contextIds.push(ctx.dialog_no);
			}
		});
	}

	// NEW FORMAT: Use highlighted_dialog_nos
	if (item.highlighted_dialog_nos && Array.isArray(item.highlighted_dialog_nos)) {
		item.highlighted_dialog_nos.forEach((dialog) => {
			const dialogNo = parseInt(dialog.dialog_no);
			if (!isNaN(dialogNo) && !contextIds.includes(dialogNo)) {
				contextIds.push(dialogNo);
			}
		});
	}

	// NEW FORMAT: Add first_dialog_no if present
	if (item.first_dialog_no) {
		const firstDialogNo = parseInt(item.first_dialog_no);
		if (!isNaN(firstDialogNo) && !contextIds.includes(firstDialogNo)) {
			contextIds.unshift(firstDialogNo); // Add to beginning
		}
	}

	return contextIds.sort((a, b) => a - b);
};

const ReferenceDrawer: React.FC<IReferenceDrawerProps> = ({
	onClose,
	referenceDrawerVisibility,
	referenceActiveTab,
	referenceItems,
	isExpanded,
	studyType,
	setIsExpanded,
	activeCitation,
	setActiveCitation,
	calculateScrollRatio,
}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const { interfaceLanguage, allStudyTypes } = useSelector((state: RootState) => state.studiesSlice);
	const { countSelectedQuote, countSelectedType, isCountWidgetOpen } = useSelector((state: RootState) => state.countWidgetSlice);
	const { activeCitationReferences, isStandardQueryMode, referenceSelectedItemTrigger, autoOpenTranscriptItem } = useSelector((state: RootState) => state.askKuralSlice);

	const [selectedItem, setSelectedItem] = useState<IReferenceItem | null>(null);
	const [referenceItem, setReferenceItem] = useState<ITranscriptItem[]>([]);
	const [pdfUrl, setPdfUrl] = useState<string>("");
	const [uploadDetails, setUploadDetails] = useState<any>({});
	const [isLoadingTranscript, setIsLoadingTranscript] = useState<boolean>(false);
	const [isLoadingReport, setIsLoadingReport] = useState<boolean>(false);
	const [activeTab, setActiveTab] = useState<StudyType>(() => {
		const filteredStudyTypes = studyType?.filter((type) => type !== StudyType.Quant);
		if (referenceActiveTab && referenceActiveTab !== StudyType.Quant) {
			return referenceActiveTab;
		}
		return filteredStudyTypes?.length > 0 ? filteredStudyTypes[0] : null;
	});
	const [isAudioVideoMode, setIsAudioVideoMode] = useState<boolean>(false);
	const [avTranscriptItems, setAvTranscriptItems] = useState<IAudioVideoTranscriptItem[]>([]);
	const [avNarrations, setAvNarrations] = useState<IAudioVideoNarration[]>([]);
	const [avMediaUrl, setAvMediaUrl] = useState<string>("");
	const [avMediaType, setAvMediaType] = useState<MediaType | undefined>(undefined);
	const [isLoadingAV, setIsLoadingAV] = useState<boolean>(false);
	const [avPresignedSegmentUrls, setAvPresignedSegmentUrls] = useState<string[]>([]);
	const { isFeatureAvailable } = useFeatures();
	const isAudioVideoEnabled = isFeatureAvailable(FeatureKeysType.AudioVideo); // TODO: Remove once created from BE
	const { trackAction } = useActionTracking(ModuleTypes.AskKural);

	// Determine which reference source to use
	const isNewCitationMode = useMemo(() => {
		return activeCitationReferences !== null && activeCitationReferences?.references?.length > 0;
	}, [activeCitationReferences]);

	// Convert new citation references to IReferenceItem format
	const convertedNewReferences = useMemo(() => {
		if (!isNewCitationMode || !activeCitationReferences) {
			return null;
		}

		const { references, studyType: citationStudyType } = activeCitationReferences;

		// Group by study type
		const groupedRefs: { [key in StudyType]?: IReferenceItem[] } = {};

		references.forEach((ref: ICitationReference) => {
			// Convert ICitationReference to IReferenceItem format
			const referenceItem: IReferenceItem = {
				tr_id: ref.tr_id || ref.notes_id || "",
				entity_id: ref.entity_id,
				file_id: ref.file_id,
				entity_name: ref.entity_name,
				file_name: ref.file_name,
				file_type: ref.file_type,
				tags: ref.tags || [],
				// NEW FORMAT: Store context_preview as string
				context_preview: ref.context_preview || { text: "", text_highlights: [] },
				context_preview_image: ref?.context_preview_image,
				notes_text: ref?.notes_text,
				// NEW FORMAT: Store highlighted_dialog_nos for highlighting logic
				highlighted_dialog_nos: ref.highlighted_dialog_nos,
				first_dialog_no: ref.first_dialog_no,

				// Build context array from highlighted_dialog_nos for backward compatibility
				context:
					ref.highlighted_dialog_nos?.map((dialog) => ({
						dialog_no: parseInt(dialog.dialog_no),
						text: "", // Will be populated when transcript is fetched
						speaker_type: undefined,
					})) || [],

				is_study_access_available: ref.is_study_access_available ?? true,
				pdf_url: ref.pdf_url,
				page_no: ref.page_no,
				citation_id: 0,
				is_audio_video: ref.is_audio_video === true,
				media_url: ref.media_url,
				media_type: ref.media_type,
				media_duration: ref.media_duration,
				media_duration_seconds: ref.media_duration_seconds,
			};

			const type = (ref.citation_type || citationStudyType) as StudyType;
			if (!groupedRefs[type]) {
				groupedRefs[type] = [];
			}
			referenceItem.citation_id = groupedRefs[type].length + 1;
			groupedRefs[type].push(referenceItem);
		});

		return groupedRefs;
	}, [isNewCitationMode, activeCitationReferences]);

	// Choose which references to display
	const displayReferences = useMemo(() => {
		if (isNewCitationMode && convertedNewReferences) {
			return convertedNewReferences;
		}
		return referenceItems;
	}, [isNewCitationMode, convertedNewReferences, referenceItems]);

	// Memoized context IDs with support for both formats
	const memoizedContextIds = useMemo(() => {
		if (!selectedItem) {
			return [];
		}
		const contextIds = extractContextIds(selectedItem);
		return contextIds;
	}, [selectedItem]);

	const quantCitationData: IQuantCitationRef | null = useMemo(() => {
		const activeRef = activeCitationReferences;
		if (!activeRef?.references) return null;

		// Check if this is a quant citation
		// For quant citations, the references array will contain
		// objects with fe_payload
		if (Array.isArray(activeRef.references) && activeRef.references.length > 0) {
			const firstRef = activeRef.references[0];
			if (isQuantCitation(firstRef)) {
				return firstRef as IQuantCitationRef;
			}
		}

		return null;
	}, [activeCitationReferences]);

	// Handle drawer close - clear new citation state
	const handleDrawerClose = useCallback(() => {
		dispatch(resetActiveCitationReferences());
		onClose();
	}, [dispatch, onClose]);

	const handleFileCloseMemoized = useCallback(() => {
		setSelectedItem(null);
		setIsAudioVideoMode(false);
		setAvTranscriptItems([]);
		setAvNarrations([]);
		setAvMediaUrl("");
		setAvMediaType(undefined);
		setAvPresignedSegmentUrls([]);
		if (countSelectedQuote) {
			dispatch(setReferenceDrawerVisibility(ReferenceDrawerVisibilityType.hidden));
		}
		dispatch(setCountSelectedQuote(null));
	}, [countSelectedQuote, dispatch]);

	const handleExpandClickMemoized = useCallback(() => {
		calculateScrollRatio();
		setIsExpanded((prev) => {
			// When prev is true we are currently shifted-expanded → collapse to expanded
			// When prev is false we are currently expanded → shift to shifted-expanded
			const nextVisibility = prev ? ReferenceDrawerVisibilityType.expanded : ReferenceDrawerVisibilityType.shiftedExpanded;
			dispatch(setReferenceDrawerVisibility(nextVisibility));
			dispatch(setReportsReferenceDrawerVisibility(nextVisibility));
			return !prev;
		});
	}, [calculateScrollRatio, setIsExpanded]);

	const { addOrUpdate, getItem } = useIndexedDB("reportCacheDB", "files");

	// Update active tab when drawer opens with new citation
	useEffect(() => {
		if (isNewCitationMode && activeCitationReferences) {
			setActiveTab(activeCitationReferences.studyType as StudyType);
		} else if (!countSelectedQuote && referenceActiveTab) {
			setActiveTab(referenceActiveTab);
		}
	}, [isNewCitationMode, activeCitationReferences, referenceActiveTab, countSelectedQuote]);

	useEffect(() => {
		if (!countSelectedQuote) {
			handleFileCloseMemoized();
		}
		if (referenceActiveTab) {
			setActiveTab(referenceActiveTab);
		}
	}, [activeCitation, referenceActiveTab]);

	useEffect(() => {
		if (countSelectedQuote !== null && countSelectedType[0] === StudyType.Transcripts) {
			setActiveTab(countSelectedType[0]);

			const isAV = isAudioVideoEnabled && countSelectedQuote?.is_audio_video === true;

			if (isAV) {
				// Transform counts-widget reference_docs schema → highlighted_dialog_nos schema
				// expected by AudioVideoTranscriptView. ConversationQuote pre-transforms when
				// possible, but we also handle the raw format as a defensive fallback.
				const rawHighlighted = countSelectedQuote?.highlighted_dialog_nos;
				const rawReferenceDocs = countSelectedQuote?.reference_docs ?? countSelectedQuote?.context;
				let highlightedDialogNos = [];

				if (Array.isArray(rawHighlighted) && rawHighlighted.length > 0) {
					// Already transformed (by ConversationQuote or chat reference format)
					highlightedDialogNos = rawHighlighted.map((d) => ({
						dialog_no: String(d.dialog_no),
						dialog_highlights: Array.isArray(d.dialog_highlights)
							? d.dialog_highlights.map((h) => ({
									start_index: h.start_index ?? h.start,
									end_index: h.end_index ?? h.end,
								}))
							: [],
					}));
				} else if (Array.isArray(rawReferenceDocs) && rawReferenceDocs.length > 0) {
					// Raw counts-widget format: reference_docs with match[]
					highlightedDialogNos = rawReferenceDocs
						.filter((doc) => doc.dialog_no != null)
						.map((doc) => ({
							dialog_no: String(doc.dialog_no),
							dialog_highlights: Array.isArray(doc.match)
								? doc.match.map((m) => ({
										start_index: m.start,
										end_index: m.end,
									}))
								: [],
						}));
				}

				const firstDialogNo =
					countSelectedQuote?.first_dialog_no != null
						? String(countSelectedQuote.first_dialog_no)
						: countSelectedQuote?.context_preview?.dialog_no != null
							? String(countSelectedQuote.context_preview.dialog_no)
							: highlightedDialogNos.length > 0
								? highlightedDialogNos[0].dialog_no
								: null;

				const avItem: IReferenceItem = {
					tr_id: countSelectedQuote?.tr_id || "",
					file_id: countSelectedQuote?.file_id || "",
					entity_id: countSelectedQuote?.entity_id || countSelectedQuote?.study_id || "",
					file_name: countSelectedQuote?.file_name || countSelectedQuote?.transcript_name || "",
					file_type: countSelectedQuote?.file_type || "",
					tags: countSelectedQuote?.tags || [],
					context_preview: countSelectedQuote?.context_preview || { text: "", text_highlights: [] },
					context: [],
					highlighted_dialog_nos: highlightedDialogNos,
					first_dialog_no: firstDialogNo,
					is_study_access_available: countSelectedQuote?.is_study_access_available ?? true,
					is_audio_video: true,
					media_url: countSelectedQuote?.media_url,
					media_type: countSelectedQuote?.media_type,
					media_duration: countSelectedQuote?.media_duration,
					media_duration_seconds: countSelectedQuote?.media_duration_seconds,
					citation_id: 0,
				};
				handleTranscriptClick(avItem);
				return;
			}

			if (isStandardQueryMode) {
				const highlighted_dialog_nos = countSelectedQuote?.context?.map((item) => ({
					dialog_no: item?.dialog_no,
					dialog_highlights: item?.match?.map((e) => ({
						start_index: e.start,
						end_index: e.end,
					})),
				}));
				const newFormatCountSelectQuote = {
					...countSelectedQuote,
					first_dialog_no: countSelectedQuote?.context_preview?.dialog_no,
					highlighted_dialog_nos: [...highlighted_dialog_nos],
				};
				setSelectedItem(newFormatCountSelectQuote);
			} else {
				setSelectedItem(countSelectedQuote);
			}

			fetchReferenceData({
				id: countSelectedQuote?.tr_id,
				entity_id: countSelectedQuote?.entity_id || countSelectedQuote?.study_id,
				file_id: countSelectedQuote?.file_id,
				studyType: countSelectedType[0],
			});
		}
	}, [countSelectedQuote, isStandardQueryMode]);

	useEffect(() => {
		if (isStandardQueryMode && referenceSelectedItemTrigger && countSelectedQuote === null) {
			setSelectedItem(null);
		}
	}, [referenceSelectedItemTrigger, isStandardQueryMode, countSelectedQuote]);

	useEffect(() => {
		if (referenceDrawerVisibility === ReferenceDrawerVisibilityType.expanded && isExpanded) {
			setIsExpanded(false);
		} else if (referenceDrawerVisibility === ReferenceDrawerVisibilityType.shiftedExpanded && !isExpanded) {
			setIsExpanded(true);
		}
	}, [referenceDrawerVisibility, isExpanded, setIsExpanded]);

	// Auto-open transcript when autoOpenTranscriptItem is set from popover
	useEffect(() => {
		if (autoOpenTranscriptItem && referenceDrawerVisibility !== ReferenceDrawerVisibilityType.hidden && !isLoadingTranscript && !selectedItem) {
			const citationType = activeCitationReferences?.studyType as StudyType;
			const itemToOpen: IReferenceItem = {
				tr_id: autoOpenTranscriptItem.tr_id || autoOpenTranscriptItem.notes_id || "",
				entity_id: autoOpenTranscriptItem.entity_id,
				file_id: autoOpenTranscriptItem.file_id,
				entity_name: autoOpenTranscriptItem.entity_name,
				file_name: autoOpenTranscriptItem.file_name || "",
				file_type: autoOpenTranscriptItem.file_type || "",
				tags: autoOpenTranscriptItem.tags || [],
				context_preview: autoOpenTranscriptItem.context_preview || "",
				notes_text: autoOpenTranscriptItem.notes_text,
				highlighted_dialog_nos: autoOpenTranscriptItem.highlighted_dialog_nos || [],
				first_dialog_no: autoOpenTranscriptItem.first_dialog_no,
				context:
					autoOpenTranscriptItem.highlighted_dialog_nos?.map((d) => ({
						dialog_no: parseInt(d.dialog_no, 10),
						text: "",
						speaker_type: "",
					})) || [],
				pdf_url: autoOpenTranscriptItem.pdf_url,
				page_no: autoOpenTranscriptItem.page_no,
				is_study_access_available: autoOpenTranscriptItem.is_study_access_available ?? true,
				citation_id: 0,
				// AV fields:
				is_audio_video: autoOpenTranscriptItem.is_audio_video === true,
				media_url: autoOpenTranscriptItem.media_url,
				media_type: autoOpenTranscriptItem.media_type,
				media_duration: autoOpenTranscriptItem.media_duration,
				media_duration_seconds: autoOpenTranscriptItem.media_duration_seconds,
			};

			// Set tab directly to avoid stale closure in handleTranscriptClick
			if (citationType) {
				setActiveTab(citationType);
			}

			if (isAudioVideoEnabled && autoOpenTranscriptItem.is_audio_video) {
				handleTranscriptClick(itemToOpen);
				dispatch(clearAutoOpenTranscriptItem());
				return;
			}

			if (citationType === StudyType.Notes || citationType === StudyType.Web_Boards) {
				// Notes don't have a detail view — just open the drawer and scroll to the matching item
				setSelectedItem(null);
				setIsExpanded(false);
				dispatch(setReferenceDrawerVisibility(ReferenceDrawerVisibilityType.expanded));

				// Find matching converted reference to get its sequential citation_id for scroll
				const notesRefs = convertedNewReferences?.[citationType];
				if (notesRefs) {
					const matchedRef = notesRefs.find((ref) => {
						if (autoOpenTranscriptItem.notes_text && ref.notes_text) {
							return ref.notes_text === autoOpenTranscriptItem.notes_text;
						}
						return ref.tr_id === (autoOpenTranscriptItem.notes_id || autoOpenTranscriptItem.tr_id) && ref.entity_id === autoOpenTranscriptItem.entity_id;
					});
					if (matchedRef) {
						setActiveCitation((prev) => ({
							...prev,
							[citationType]: matchedRef.citation_id,
						}));
					}
				}
			} else {
				// Transcripts/Reports — open detail view as before
				handleTranscriptClick(itemToOpen);
			}

			dispatch(clearAutoOpenTranscriptItem());
		}
	}, [
		autoOpenTranscriptItem,
		referenceDrawerVisibility,
		isLoadingTranscript,
		selectedItem,
		activeCitationReferences,
		convertedNewReferences,
		dispatch,
		handleTranscriptClick,
		setActiveCitation,
		setIsExpanded,
	]);

	const fetchReferenceData = async (params: { id: string; entity_id: string; file_id?: string; studyType?: string }) => {
		const { id, entity_id, file_id, studyType } = params;
		setPdfUrl("");
		const cacheKey = `pdf-${id}`;
		setReferenceItem([]);
		const isValidTranscript = activeTab === StudyType.Transcripts;
		try {
			setIsLoadingReport(true);
			setIsLoadingTranscript(true);

			if (activeTab === StudyType.Transcripts) {
				const response: any = await getReference({
					id,
					study_id: entity_id,
					file_id,
					file_type: activeTab,
					lang_code: interfaceLanguage?.code || SupportedLanguageType.English,
				});
				trackAction(ActionTypes.REFERENCE, {
					feature: true,
					subAction: isValidTranscript ? ActionTypes.VIEW_TRANSCRIPT : ActionTypes.VIEW_REPORT,
					type: undefined,
				});
				if (response) {
					const data = response?.data;
					if (Array.isArray(data)) {
						setReferenceItem(data);
					} else if (Array.isArray(data?.transcript_data)) {
						setReferenceItem(data.transcript_data);
					} else {
						setReferenceItem([]);
					}
					setUploadDetails(data?.upload_details);
					setIsLoadingTranscript(false);
				}
			} else if (studyType === StudyType.Transcripts) {
				const response: any = await getReference({
					id: id,
					study_id: entity_id,
					file_id,
					file_type: studyType,
					lang_code: interfaceLanguage?.code || SupportedLanguageType.English,
				});
				if (response) {
					const data = response?.data;
					if (Array.isArray(data)) {
						setReferenceItem(data);
					} else if (Array.isArray(data?.transcript_data)) {
						setReferenceItem(data.transcript_data);
					} else {
						setReferenceItem([]);
					}
					setUploadDetails(data?.upload_details);
					setIsLoadingTranscript(false);
				}
			} else {
				// Handle Reports
				try {
					const cachedFile = await getItem(cacheKey);

					if (cachedFile) {
						setPdfUrl(URL.createObjectURL(cachedFile?.fileBlob));
						setIsLoadingReport(false);
					} else {
						const response: any = await getReference({
							id: id,
							study_id: entity_id,
							file_id,
							file_type: activeTab,
							lang_code: interfaceLanguage?.code || SupportedLanguageType.English,
						});
						const presignedUrl = response?.data?.url;

						if (response && presignedUrl) {
							setPdfUrl(presignedUrl);
							trackAction(ActionTypes.REFERENCE, {
								feature: true,
								subAction: ActionTypes.VIEW_REPORT,
								type: undefined,
							});

							// Cache the file
							try {
								const fileResponse = await fetch(assertSafeHttpUrl(presignedUrl));
								if (fileResponse?.ok) {
									const fileBlob = await fileResponse.blob();
									await addOrUpdate({ id: cacheKey, fileBlob });
								}
							} catch (cacheError) {
								console.warn("Failed to cache file:", cacheError);
								// Continue even if caching fails
							}

							setIsLoadingReport(false);
						}
					}
				} catch (cacheError) {
					console.error("Cache error:", cacheError);
					// If cache fails, try fetching from server anyway
					const response: any = await getReference({
						id: id,
						study_id: entity_id,
						file_id,
						file_type: activeTab,
						lang_code: interfaceLanguage?.code || SupportedLanguageType.English,
					});
					const presignedUrl = response?.data?.url;

					if (response && presignedUrl) {
						setPdfUrl(presignedUrl);
						setIsLoadingReport(false);
					}
				}
			}
		} catch (error) {
			console.error("Error:", error);
		} finally {
			setIsLoadingReport(false);
			setIsLoadingTranscript(false);
		}
	};

	// Tracks mount state so async fetches don't setState on an unmounted component (review A11).
	const isMountedRef = useRef(true);
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	const fetchAudioVideoData = useCallback(
		async (item: IReferenceItem) => {
			setIsLoadingAV(true);
			setAvTranscriptItems([]);
			setAvNarrations([]);
			setAvMediaUrl("");
			setAvMediaType(undefined);
			setAvPresignedSegmentUrls([]);
			try {
				const response: any = await getReference({
					id: item?.tr_id || "",
					study_id: item?.entity_id || "",
					file_id: item?.file_id,
					file_type: StudyType.Transcripts,
					lang_code: interfaceLanguage?.code || SupportedLanguageType.English,
				});
				if (!isMountedRef.current) return;
				if (response?.data) {
					const { media_data, transcript_data, narrations } = response.data;

					const transcriptArr: IAudioVideoTranscriptItem[] = (transcript_data || []).map((d: any) => ({
						...d,
						timestamp_start: d.timestamp_start !== null && d.timestamp_start !== undefined ? parseFloat(d.timestamp_start) : null,
						timestamp_end: d.timestamp_end !== null && d.timestamp_end !== undefined ? parseFloat(d.timestamp_end) : null,
					}));
					setAvTranscriptItems(transcriptArr);
					setAvNarrations(Array.isArray(narrations) ? narrations : []);
					setAvPresignedSegmentUrls(media_data?.video_presigned || []);
					setAvMediaUrl(media_data?.media_url || "");
					setAvMediaType(media_data?.media_type as MediaType | undefined);
				}
			} catch (error) {
				if (!isMountedRef.current) return;
				console.error("Error fetching audio/video data:", error);
			} finally {
				if (isMountedRef.current) setIsLoadingAV(false);
			}
		},
		[interfaceLanguage],
	);

	const handleTranscriptClick = useCallback(
		(id: IReferenceItem) => {
			if (!id?.is_study_access_available) return;

			if (isAudioVideoEnabled && id?.is_audio_video) {
				setSelectedItem(id);
				setIsAudioVideoMode(true);
				if (referenceDrawerVisibility !== ReferenceDrawerVisibilityType.expanded) {
					dispatch(setReferenceDrawerVisibility(ReferenceDrawerVisibilityType.expanded));
					dispatch(setReportsReferenceDrawerVisibility(ReferenceDrawerVisibilityType.expanded));
				}
				if (isExpanded) {
					setIsExpanded(false);
				}
				fetchAudioVideoData(id);
				return;
			}

			setIsAudioVideoMode(false);
			const isValidTranscript = activeTab === StudyType.Transcripts;
			setSelectedItem(id);
			setIsExpanded(true);
			dispatch(setReferenceDrawerVisibility(ReferenceDrawerVisibilityType.shiftedExpanded));
			dispatch(setReportsReferenceDrawerVisibility(ReferenceDrawerVisibilityType.shiftedExpanded));
			if (isValidTranscript) {
				fetchReferenceData({
					id: id?.tr_id,
					entity_id: id?.entity_id,
					file_id: id?.file_id,
					studyType: activeTab,
				});
			} else if (activeTab === StudyType.Notes || activeTab === StudyType.Web_Boards) {
				return;
			} else {
				fetchReferenceData({
					id: id?.pdf_url,
					entity_id: id?.entity_id,
					file_id: id?.file_id,
				});
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[activeTab, dispatch, fetchAudioVideoData, setIsExpanded, referenceDrawerVisibility, isExpanded, isAudioVideoEnabled],
	);

	const handleFileDownload = async (item: any) => {
		if (!item?.is_study_access_available) return;
		const loadingMessage = message.loading(t("Downloading"), 1000);
		try {
			const downloadObj = {
				file_type: activeTab,
				file_name: item?.file_name,
				file_extension: item?.file_type,
				study_id: item?.entity_id,
				file_id: item?.file_id,
				id: item?.pdf_url,
				lang_code: interfaceLanguage?.code || SupportedLanguageType.English,
				is_download: true,
			};
			await downloadReferenceFile(downloadObj);
		} catch (err) {
			console.error("Error:", err);
			message.error(t("An unexpected error occurred"));
		} finally {
			loadingMessage();
		}
	};

	const handleDownloadAvTranscript = useCallback(async () => {
		if (!selectedItem?.is_study_access_available) return;
		const loadingMessage = message.loading(t("Downloading"), 1000);
		try {
			const matchedStudyType = allStudyTypes?.find((type) => AskKuralStudyTypeMapping[type.key as keyof typeof AskKuralStudyTypeMapping] === (activeTab as string));
			const downloadObj: IFileDownloadPayload = {
				file_name: selectedItem?.file_name,
				file_type: fileTypes.transcript,
				study_id: selectedItem?.entity_id,
				file_id: selectedItem?.file_id,
				doc_download_format: downloadFormats.transcript,
				study_type_name: matchedStudyType?.label,
				...(!selectedItem?.file_id && { tr_id: selectedItem?.tr_id }),
			};
			await downloadFile(downloadObj);
			trackAction(ActionTypes.FILE_DOWNLOAD, {
				feature: true,
				subAction: ActionTypes.FILE_DOWNLOAD,
				type: downloadObj.file_type,
			});
		} catch (err) {
			console.error("Error:", err);
			message.error(t("An unexpected error occurred"));
		} finally {
			loadingMessage();
		}
	}, [selectedItem, activeTab, allStudyTypes, t, trackAction]);

	const handleTabChange = useCallback((activeKey) => {
		setActiveTab(activeKey as StudyType);
	}, []);

	// Update renderReferenceItems function
	const renderReferenceItems = useCallback(
		(items: IReferenceItem[]) => {
			return items?.map((item: IReferenceItem, index: number) => {
				const isNewFormat = isStandardQueryMode;

				if (selectedItem && selectedItem === item && activeTab === StudyType.Transcripts) {
					if (isNewFormat) {
						return (
							<ReflectiveTranscriptView
								key={index}
								handleExpand={handleExpandClickMemoized}
								transcriptName={selectedItem?.file_name}
								createdBy={transformNameWithDateTime(uploadDetails?.uploaded_by?.full_name, uploadDetails?.upload_dt, t)}
								transcriptItems={referenceItem}
								onClose={handleFileCloseMemoized}
								isLoadingTranscript={isLoadingTranscript}
								isExpanded={isExpanded}
								highlightedDialogs={selectedItem?.highlighted_dialog_nos || []}
								firstDialogNo={selectedItem?.first_dialog_no || null}
								contextIds={memoizedContextIds}
								module={ModuleTypes.AskKural}
							/>
						);
					} else {
						return (
							<TranscriptView
								key={index}
								handleExpand={handleExpandClickMemoized}
								transcriptName={selectedItem?.file_name}
								createdBy={transformNameWithDateTime(uploadDetails?.uploaded_by?.full_name, uploadDetails?.upload_dt, t)}
								transcriptItems={referenceItem}
								onClose={handleFileCloseMemoized}
								isLoadingTranscript={isLoadingTranscript}
								contextIds={memoizedContextIds}
								isExpanded={isExpanded}
								module={ModuleTypes.AskKural}
							/>
						);
					}
				}

				if (selectedItem && selectedItem === item && activeTab === StudyType.Reports) {
					return (
						<ReportView
							key={index}
							reportName={selectedItem?.file_name}
							pdfUrl={pdfUrl}
							onClose={handleFileCloseMemoized}
							loading={isLoadingReport}
							setLoading={setIsLoadingReport}
							pageNo={isNewFormat ? selectedItem?.page_no : selectedItem?.context_preview?.[0]?.page_no}
							isExpanded={isExpanded}
							handleExpand={handleExpandClickMemoized}
							handleFileDownload={() => handleFileDownload(selectedItem)}
						/>
					);
				}

				if (isNewFormat) {
					if (activeTab === StudyType.Transcripts || activeTab === StudyType.Reports) {
						return (
							<ReflectiveReferenceItem
								key={index}
								item={item as IReferenceItem}
								onTranscriptClick={handleTranscriptClick}
								handleFileDownload={handleFileDownload}
								activeCitationNo={activeCitation?.[activeTab] ?? 0}
								activeTab={activeTab}
								isAudioVideoEnabled={isAudioVideoEnabled}
							/>
						);
					} else if (activeTab === StudyType.Notes || activeTab === StudyType.Web_Boards) {
						return (
							<ReflectiveNotesReferenceItem
								key={index}
								item={item as IReferenceItem}
								onTranscriptClick={handleTranscriptClick}
								activeCitationNo={activeCitation?.[activeTab] ?? 0}
								activeTab={activeTab}
							/>
						);
					}
					return null;
				} else {
					return (
						<ReferenceItem
							key={index}
							item={item}
							onTranscriptClick={handleTranscriptClick}
							handleFileDownload={handleFileDownload}
							activeCitationNo={activeCitation?.[activeTab] ?? 0}
							activeTab={activeTab}
						/>
					);
				}
			});
		},
		[
			isExpanded,
			activeCitation,
			selectedItem,
			activeTab,
			uploadDetails,
			referenceItem,
			isLoadingTranscript,
			pdfUrl,
			isLoadingReport,
			handleTranscriptClick,
			handleFileCloseMemoized,
			handleFileDownload,
			memoizedContextIds,
			isStandardQueryMode,
			isAudioVideoEnabled,
		],
	);

	const renderContent = () => {
		// If quant citation is active, render the quant chart drawer
		if (quantCitationData) {
			return <QuantCitationDrawer quantCitationData={quantCitationData} onClose={handleDrawerClose} isExpanded={isExpanded} handleExpand={handleExpandClickMemoized} />;
		}

		if (selectedItem) {
			if (isAudioVideoEnabled && isAudioVideoMode) {
				return (
					<AudioVideoTranscriptView
						item={selectedItem}
						onClose={handleFileCloseMemoized}
						isExpanded={isExpanded}
						handleExpand={handleExpandClickMemoized}
						transcriptItems={avTranscriptItems}
						narrations={avNarrations}
						mediaUrl={avMediaUrl}
						mediaType={avMediaType}
						isLoadingAV={isLoadingAV}
						highlightedDialogs={selectedItem?.highlighted_dialog_nos || []}
						firstDialogNo={selectedItem?.first_dialog_no || null}
						presignedSegmentUrls={avPresignedSegmentUrls}
						onDownloadTranscript={handleDownloadAvTranscript}
					/>
				);
			}

			if (activeTab === StudyType.Transcripts) {
				if (isStandardQueryMode) {
					return (
						<ReflectiveTranscriptView
							handleExpand={handleExpandClickMemoized}
							transcriptName={selectedItem?.file_name}
							createdBy={transformNameWithDateTime(uploadDetails?.uploaded_by?.full_name, uploadDetails?.upload_dt, t)}
							transcriptItems={referenceItem}
							onClose={handleFileCloseMemoized}
							isLoadingTranscript={isLoadingTranscript}
							isExpanded={isExpanded}
							highlightedDialogs={selectedItem?.highlighted_dialog_nos || []}
							firstDialogNo={selectedItem?.first_dialog_no || null}
							module={ModuleTypes.AskKural}
						/>
					);
				} else {
					return (
						<TranscriptView
							handleExpand={handleExpandClickMemoized}
							transcriptName={selectedItem?.file_name}
							createdBy={transformNameWithDateTime(uploadDetails?.uploaded_by?.full_name, uploadDetails?.upload_dt, t)}
							transcriptItems={referenceItem}
							onClose={handleFileCloseMemoized}
							isLoadingTranscript={isLoadingTranscript}
							contextIds={memoizedContextIds}
							isExpanded={isExpanded}
							module={ModuleTypes.AskKural}
						/>
					);
				}
			} else if (activeTab === StudyType.Reports) {
				return (
					<ReportView
						reportName={selectedItem?.file_name}
						pdfUrl={pdfUrl}
						onClose={handleFileCloseMemoized}
						loading={isLoadingReport}
						setLoading={setIsLoadingReport}
						pageNo={isStandardQueryMode ? selectedItem?.page_no : selectedItem?.context_preview[0]?.page_no}
						isExpanded={isExpanded}
						handleExpand={handleExpandClickMemoized}
						handleFileDownload={() => handleFileDownload(selectedItem)}
					/>
				);
			}
		}
		return renderDefaultContent();
	};

	const renderDefaultContent = () => {
		const studyTypeKeys = Object.keys(displayReferences) as StudyType[];

		if (studyTypeKeys?.length === 0) {
			return <span className="flex justify-center text-[--neutral-grey] p-4">{t("No references available")}</span>;
		}

		const renderHeader = () => (
			<div className="custom-drawer-header flex items-center bg-[--drawer-header-bg]">
				<span className="ref-drawer-title secondary-color flex px-4">
					{t("References")}
					{/* {isNewCitationMode && activeCitationReferences && <span className="ml-2 text-sm text-[--neutral-grey]">({activeCitationReferences.citationId})</span>} */}
				</span>
				<Tooltip title={t("This section covers the references that are used to generate the response to your query (sorted with respect to relevance to the query)")}>
					<i className="material-symbols-outlined flex secondary-color text-xl cursor-pointer pt-1">info</i>
				</Tooltip>
				<i className="material-symbols-outlined text-[1.5rem] cursor-pointer text-[--secondary-color] absolute right-16 mr-4" onClick={handleExpandClickMemoized}>
					{referenceDrawerVisibility === ReferenceDrawerVisibilityType.shiftedExpanded ? "hide" : "expand_content"}
				</i>
				<CloseOutlined className="p-3 text-base secondary-color cursor-pointer absolute right-0 z-50 mr-6" onClick={handleDrawerClose} />
			</div>
		);

		return (
			<>
				{renderHeader()}
				{studyTypeKeys?.length > 1 ? (
					<Tabs
						activeKey={activeTab}
						onChange={handleTabChange}
						className="ask-kural-reference-tabs"
						destroyOnHidden
						items={studyType
							?.filter(
								(key) =>
									studyTypeKeys?.includes(key) &&
									key !== StudyType.Quant &&
									displayReferences[key] &&
									Array.isArray(displayReferences[key]) &&
									displayReferences[key].length > 0,
							)
							?.map((key) => ({
								key,
								label: t(StudyTypeDisplayName[key]),
								children: (
									<div className="custom-drawer-body">
										{Array.isArray(displayReferences[key]) && displayReferences[key]?.length > 0 ? (
											renderReferenceItems(displayReferences[key])
										) : (
											<span className="flex justify-center text-[--neutral-grey] p-4">{t("No references available")}</span>
										)}
									</div>
								),
							}))}
					/>
				) : (
					<div className="custom-drawer-body single-reference-type">
						{Array.isArray(displayReferences[studyTypeKeys[0]]) && displayReferences[studyTypeKeys[0]]?.length > 0 ? (
							renderReferenceItems(displayReferences[studyTypeKeys[0]])
						) : (
							<span className="flex justify-center text-[--neutral-grey] p-4">{t("No references available")}</span>
						)}
					</div>
				)}
			</>
		);
	};

	return (
		<div className={`reference-custom-drawer ${referenceDrawerVisibility} ${isCountWidgetOpen ? "count-widget-reference" : ""}`}>
			<div className="ask-kural-custom-drawer-content">{renderContent()}</div>
		</div>
	);
};

export default React.memo(ReferenceDrawer);
